import { useState } from "react";
import { Loader2, Archive, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import api from "../../lib/api";

export default function ArchiveConfirmDialog({ open, onOpenChange, restaurant, onDone }) {
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!restaurant) return;
    setBusy(true);
    try {
      await api.archiveRestaurant(restaurant.restaurant_id);
      onDone?.(restaurant);
      onOpenChange(false);
      // Gmail-style toast with Undo (consistent with the rest of the app).
      // 8s window because archiving a whole restaurant is a heavier action
      // and the user may need a moment to react.
      toast.success(`${restaurant.name} archivado`, {
        description: "Te llegará un email con los detalles. Se eliminará en 90 días.",
        duration: 8000,
        action: {
          label: "Deshacer",
          onClick: async () => {
            try {
              await api.restoreRestaurant(restaurant.restaurant_id);
              toast.success(`${restaurant.name} restaurado`);
              onDone?.(restaurant);
            } catch (e) {
              toast.error(e?.message || "No se pudo deshacer");
            }
          },
        },
      });
    } catch (err) {
      toast.error(err.message || "Error al archivar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-amber-700" />
            ¿Archivar {restaurant?.name}?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                El restaurante desaparecerá del selector y de las vistas habituales. Podrás restaurarlo cuando quieras durante <strong>90 días</strong>.
              </p>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex gap-2 text-amber-900">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div className="text-xs">
                  Transcurridos 90 días sin restaurar, el restaurante se <strong>eliminará definitivamente</strong> de forma automática, incluyendo ventas, clientes e inventario. Te enviaremos emails de aviso.
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(e) => { e.preventDefault(); confirm(); }}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="confirm-archive-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Archivar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
